/** Reusable form controls + image upload + list toolbar for the admin pages. */
import React, { useRef, useState } from "react";
import { Upload, X, Plus, ImagePlus, Loader2 } from "lucide-react";
import { adminApi, absoluteUrl } from "../lib/api";
import { toast } from "sonner";

export const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1.5">
    {children}
  </label>
);

export const TextInput = ({ value, onChange, placeholder, type = "text", testId, ...rest }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    {...rest}
    className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/40 transition-colors"
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 4, testId }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    data-testid={testId}
    className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/40 transition-colors resize-y"
  />
);

export const NumberInput = ({ value, onChange, ...rest }) => (
  <input
    type="number"
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value))}
    {...rest}
    className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-400/40"
  />
);

export const Switch = ({ value, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className="inline-flex items-center gap-3 text-sm text-slate-300"
  >
    <span
      className={`relative w-10 h-6 rounded-full transition-colors ${value ? "bg-cyan-400/80" : "bg-white/10"}`}
    >
      <span
        className={`absolute top-0.5 ${value ? "left-[18px]" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`}
      />
    </span>
    <span>{label}</span>
  </button>
);

export const Select = ({ value, onChange, options, testId }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-400/40"
  >
    {options.map((o) => (
      <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value} className="bg-[#0a0a14]">
        {typeof o === "string" ? o : o.label}
      </option>
    ))}
  </select>
);

/** A simple "tag chips" editor for comma-separated style lists. */
export const TagEditor = ({ value, onChange, placeholder = "Add and press Enter…" }) => {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...list, t]);
    setDraft("");
  };

  return (
    <div className="flex flex-wrap items-center gap-2 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2">
      {list.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] rounded-full bg-white/[0.05] border border-white/10 text-slate-200">
          {t}
          <button
            type="button"
            onClick={() => onChange(list.filter((_, j) => j !== i))}
            className="text-slate-500 hover:text-white"
            aria-label="remove tag"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
        placeholder={placeholder}
        className="flex-1 min-w-[120px] bg-transparent text-white placeholder:text-slate-600 focus:outline-none px-1"
      />
    </div>
  );
};

/** Editor for a list of long-text bullets (one per line; rendered as separate fields). */
export const BulletEditor = ({ value, onChange, placeholder = "Add a bullet…" }) => {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {list.map((b, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="font-mono text-[10px] mt-3 text-slate-500">{String(i + 1).padStart(2, "0")}</span>
          <TextArea
            value={b}
            onChange={(v) => onChange(list.map((x, j) => (j === i ? v : x)))}
            rows={2}
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, j) => j !== i))}
            className="mt-2 text-slate-500 hover:text-red-400"
            aria-label="remove bullet"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, ""])}
        className="inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200"
      >
        <Plus size={14} /> {placeholder}
      </button>
    </div>
  );
};

/** Image upload with preview. */
export const ImageUploader = ({ value, onChange, label = "Image" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await adminApi.uploadImage(file);
      onChange(res.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-20 h-20 rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden grid place-items-center">
        {value ? (
          <img src={absoluteUrl(value)} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImagePlus size={20} className="text-slate-500" />
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-black/50">
            <Loader2 size={16} className="animate-spin text-cyan-300" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg glass text-white text-xs hover:border-cyan-400/40"
        >
          <Upload size={14} /> Upload {label}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs text-slate-500 hover:text-red-400"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export const Section = ({ title, children, action }) => (
  <div className="glass rounded-2xl p-5 md:p-6">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300">{title}</div>
      {action}
    </div>
    {children}
  </div>
);
